import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Sparkles, Loader2, ShieldCheck, Image as ImageIcon, Box, Music, Film, Store, Trash2, BadgeCheck, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAT_ICON = { image: ImageIcon, texture: ImageIcon, "3d": Box, audio: Music, animation: Film, marketplace: Store };

export default function AssetHub() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [provider, setProvider] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);
  const timer = useRef(null);

  const loadAssets = useCallback(async () => {
    try { const { data } = await api.get("/asset-hub/assets"); setAssets(data); } catch {}
  }, []);

  useEffect(() => {
    api.get("/asset-hub/providers").then((r) => setProviders(r.data)).catch(() => {});
    loadAssets();
    timer.current = setInterval(loadAssets, 5000);
    return () => clearInterval(timer.current);
  }, [loadAssets]);

  const genProviders = providers.filter((p) => p.enabled && p.configured && ["image", "texture", "3d"].includes(p.category));
  const sel = providers.find((p) => p.id === provider);

  const generate = async () => {
    if (!provider) { toast.error("Pick a generator first."); return; }
    if (prompt.trim().length < 2) { toast.error("Describe what you want to create."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/asset-hub/generate", { provider, prompt: prompt.trim() });
      if (data.kind === "3d") { toast.success(data.message || "Building your 3D asset…"); }
      else { toast.success("Asset generated and saved to your library ✨"); }
      setPrompt(""); loadAssets();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runCheck = async (id) => {
    try { const { data } = await api.post(`/asset-hub/assets/${id}/check`); setCheck(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const byCat = providers.reduce((acc, p) => { (acc[p.category] = acc[p.category] || []).push(p); return acc; }, {});

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="asset-hub-page">
      <PageHeader title="Asset API Hub" icon={Boxes} subtitle="One hub to generate licensed, game-ready assets — art, textures, 3D models and more. Every asset is tracked for rights & ownership." />

      {/* Generate panel */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-6" data-testid="hub-generate-panel">
        <div className="flex flex-wrap gap-2 mb-3">
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="w-full sm:w-72" data-testid="hub-provider-select"><SelectValue placeholder="Choose a generator…" /></SelectTrigger>
            <SelectContent>
              {genProviders.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.credit_cost} cr</SelectItem>)}
            </SelectContent>
          </Select>
          {sel && <Badge variant="secondary" className="self-center" data-testid="hub-cost-badge">{sel.credit_cost} credits · {sel.category}</Badge>}
        </div>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="hub-prompt"
          placeholder={sel?.category === "texture" ? "e.g. mossy stone wall, lava rock, snowy ground" : "Describe one asset — e.g. 'a glowing crystal sword' or 'a fox warrior in purple armour'"}
          className="min-h-[72px] mb-3" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-[hsl(var(--muted-foreground))]">{sel ? `Costs ${sel.credit_cost} AI credits` : "Pick a generator to see the cost"}</span>
          <Button onClick={generate} disabled={busy || !provider} className="rounded-full ml-auto bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="hub-generate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate asset
          </Button>
        </div>
      </div>

      {/* Provider catalog */}
      <h2 className="font-display text-lg font-bold mb-3">Connected providers</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-testid="hub-providers">
        {Object.entries(byCat).map(([cat, ps]) => ps.map((p) => {
          const Icon = CAT_ICON[cat] || Boxes;
          return (
            <div key={p.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`hub-provider-${p.id}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))]/10 grid place-items-center"><Icon className="w-5 h-5 text-[hsl(var(--primary))]" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{p.provider}</div>
                </div>
                {p.status === "coming_soon" ? <Badge variant="secondary" className="text-[10px]">Soon</Badge>
                  : !p.configured ? <Badge variant="secondary" className="text-[10px]"><Lock className="w-3 h-3" /></Badge>
                  : p.enabled ? <Badge className="bg-emerald-500 text-[10px]">On</Badge>
                  : <Badge variant="destructive" className="text-[10px]">Off</Badge>}
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {p.caps.slice(0, 4).map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--muted))]">{c}</span>)}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[hsl(var(--muted-foreground))]">
                <span className="font-semibold text-[hsl(var(--foreground))]">{p.credit_cost} cr</span>
                {p.license?.commercial_use && <span className="flex items-center gap-0.5 text-emerald-500"><BadgeCheck className="w-3 h-3" /> Commercial</span>}
                {p.license?.marketplace_allowed && <span className="text-[hsl(var(--primary))]">Resale OK</span>}
              </div>
            </div>
          );
        }))}
      </div>

      {/* Library */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-bold">Your hub assets</h2>
        <button onClick={() => navigate("/app/3d")} className="text-xs text-[hsl(var(--primary))] font-semibold">3D Studio →</button>
      </div>
      {assets.length === 0 ? (
        <EmptyState emoji="🧰" title="No hub assets yet" text="Generate art, textures or 3D models above — they'll appear here with full licence info." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hub-library">
          {assets.map((a) => (
            <div key={a.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover" data-testid={`hub-asset-${a.id}`}>
              <div className="aspect-square bg-[hsl(var(--muted))] grid place-items-center">
                {a.kind === "3d" ? (
                  a.status === "succeeded" && a.thumbnail_url ? <img src={a.thumbnail_url} alt={a.name} className="w-full h-full object-cover" />
                  : <div className="text-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))] mx-auto mb-1" /><div className="text-[10px]">{a.status === "failed" ? "Failed" : `${a.progress || 0}%`}</div></div>
                ) : <img src={`${BACKEND}${a.url}`} alt={a.name} className="w-full h-full object-cover" loading="lazy" />}
              </div>
              <div className="p-2.5">
                <div className="font-semibold text-xs truncate">{a.name}</div>
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                  <Badge variant="secondary" className="text-[9px] capitalize">{a.kind || a.type}</Badge>
                  {a.license?.commercial_use && <Badge className="bg-emerald-500 text-[9px]">Commercial</Badge>}
                </div>
                <Button size="sm" variant="outline" className="w-full rounded-full mt-2 text-xs h-7" onClick={() => runCheck(a.id)} data-testid={`hub-check-${a.id}`}>
                  <ShieldCheck className="w-3.5 h-3.5" /> Check rights
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QC dialog */}
      <Dialog open={!!check} onOpenChange={(o) => !o && setCheck(null)}>
        <DialogContent className="sm:max-w-md" data-testid="hub-check-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[hsl(var(--primary))]" /> Asset quality & rights</DialogTitle><DialogDescription className="sr-only">Licensing, ownership and quality details for this asset.</DialogDescription></DialogHeader>
          {check && (
            <div className="space-y-2">
              <div className={`text-sm font-semibold ${check.passed ? "text-emerald-500" : "text-amber-500"}`}>{check.passed ? "✅ All clear — safe to use & publish" : "⚠️ Some items need attention"}</div>
              {check.checks.map((c) => (
                <div key={c.label} className="flex items-center gap-2 text-sm">
                  <span className={c.ok ? "text-emerald-500" : "text-red-500"}>{c.ok ? "✓" : "✗"}</span>{c.label}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
